import {Injectable} from '@angular/core';
import {debounceTime, distinctUntilChanged, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {ProjectViewModel} from '../../features/user/garage/project.view-model';
import {UserRestService} from '../../api/services/user.rest.service';
import {ProjectRestService} from '../../api/services/project.rest.service';
import {defer, forkJoin, Observable, of, Subject} from 'rxjs';
import {ResultListApiModel} from '../../api/models/result-list.api.model';
import {ProjectApiModel} from '../../api/models/project.api-model';
import {UserApiModel} from '../../api/models/user.api-model';
import {UserViewModel} from '../../features/user/settings/user.view-model';
import {QueryResourceInterface} from '../models/query-resource.interface';
import {MediaObjectViewModel} from '../models/media-object.view-model';
import {QueryParamsApiModel} from '../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../api/models/filter-parameter.api.model';
import {MediaObjectApiModel} from '../../api/models/media-object.api-model';
import {MediaObjectRestService} from '../../api/services/media-object.rest.service';
import {VehicleApiModel} from '../../api/models/vehicle.api-model';
import {VehicleViewModel} from '../../features/user/garage/vehicle.view-model';
import {VehicleTypeViewModel} from '../models/vehicle-type.view-model';
import {VehicleTypeApiModel} from '../../api/models/vehicle-type.api-model';
import {VehicleRestService} from '../../api/services/vehicle.rest.service';
import {VehicleTypeRestService} from '../../api/services/vehicle-type.rest.service';
import {
  PersonalInformationViewModel
} from '../../features/user/settings/personal-information/personal-information-view-model';
import {SearchLogRestService} from '../../api/services/search-log.rest.service';
import {CurrentUserViewModel} from '../models/current-user.view-model';
import {SearchLogApiModel} from '../../api/models/search-log.api-model';
import {plainToClass} from 'class-transformer';
import {MediaObjectPipe} from '../pipes/media-object.pipe';
import {EventRestService} from '../../api/services/event.rest.service';
import {EventApiModel} from '../../api/models/event.api.model';
import {ParticipantApiModel} from '../../api/models/participant.api.model';
import {ArrayHelper} from '../helpers/array.helper';
import {OffersDataProviderService} from "../../shared/marketplace/offers.data-provider.service";
import {MarketplaceAdvertisementApiModel} from "../../api/models/marketplace-advertisement.api.model";
import {VehicleTypeEnum} from "../enums/vehicle-type.enum";
import {QueryParamsService} from "./query-params.service";
import {GlobalSearchRestService} from "../../api/services/global-search.rest.service";
import {GlobalSearchItemApiModel} from "../../api/models/global-search-result.api.model";


@Injectable({
  providedIn: 'root'
})
export class QueryService {
  public totalItems: {[key: string]: number} = {all: 0, posts: 0, projects: 0, users: 0, events: 0, marketplace: 0};
  public posts: QueryResourceInterface[] = [];
  public projects: QueryResourceInterface[] = [];
  public users: QueryResourceInterface[] = [];
  public events: QueryResourceInterface[] = [];
  public marketplace: QueryResourceInterface[] = [];
  public resources: QueryResourceInterface[] = [];
  public resultSubject$: Subject<string> = new Subject();
  public hasLastQueriedResources: boolean;
  public loading: boolean = true;


  constructor(
    private readonly projectRS: ProjectRestService,
    private readonly userRS: UserRestService,
    private readonly eventRS: EventRestService,
    private readonly vehicleRS: VehicleRestService,
    private readonly vehicleTypeRS: VehicleTypeRestService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly searchLogRS: SearchLogRestService,
    private readonly offersDPS: OffersDataProviderService,
    private readonly globalSearchRS: GlobalSearchRestService
  ) {
  }

  private static sortByName(a: QueryResourceInterface, b: QueryResourceInterface): number {
    return ('' + a.name).localeCompare(b.name);
  }

  public createLog(user: CurrentUserViewModel, queryResource: QueryResourceInterface): Observable<any> {
    const searchLogAM = plainToClass(SearchLogApiModel, {
      user: user['@id'],
      type: queryResource.type,
      slug: queryResource.slug
    });
    return this.searchLogRS.create(searchLogAM).pipe(mergeMap(() => {
      return this.fetchSearchLogResources(user);
    }));
  }

  public fetchSearchLogResources(userVM: CurrentUserViewModel): Observable<(ProjectViewModel[]|UserViewModel[])[]> {
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('id', userVM.id, 'single'));
    return this.searchLogRS.getCollection(queryParams).pipe(switchMap((searchLogRL: ResultListApiModel<SearchLogApiModel>) => {
      if (searchLogRL.total) {
        this.hasLastQueriedResources = true;
        const projectQueryParams = new QueryParamsApiModel();
        const userQueryParams = new QueryParamsApiModel();
        const eventQueryParams = new QueryParamsApiModel();
        const offerQueryParams = new QueryParamsApiModel();
        searchLogRL.records.forEach(e => {
          switch (e.type) {
            case 'user': userQueryParams.filters.push(new FilterParameterApiModel('slug', e.slug, 'array')); break;
            case 'project': projectQueryParams.filters.push(new FilterParameterApiModel('slug', e.slug, 'array')); break;
            case 'event': eventQueryParams.filters.push(new FilterParameterApiModel('slug', e.slug, 'array')); break;
            case 'marketplace': offerQueryParams.filters.push(new FilterParameterApiModel('slug', e.slug, 'array')); break;
          }
        });

        return forkJoin([
          projectQueryParams.filters.length ? this.projectRS.getCollection(projectQueryParams) : of(new ResultListApiModel<ProjectApiModel>(ProjectApiModel)),
          userQueryParams.filters.length ? this.userRS.getCollection(userQueryParams) : of (new ResultListApiModel<UserApiModel>(UserApiModel)),
          eventQueryParams.filters.length ? this.eventRS.getCollection(eventQueryParams) : of (new ResultListApiModel<EventApiModel>(EventApiModel)),
          offerQueryParams.filters.length ? this.offersDPS.getCollection(offerQueryParams, false, null) : of (new ResultListApiModel<MarketplaceAdvertisementApiModel>(MarketplaceAdvertisementApiModel))
        ]).pipe(mergeMap(([projectsAM, usersAM, eventsAM, offersAM]: [ResultListApiModel<ProjectApiModel>, ResultListApiModel<UserApiModel>, ResultListApiModel<EventApiModel>,  ResultListApiModel<MarketplaceAdvertisementApiModel>]) => {
        return this.forkDeps([projectsAM, usersAM, eventsAM, offersAM], false).pipe(tap(() => {
            this.resources = searchLogRL.records
              .map((logEntry) => {
                const queryResource = this.resources.find((resource) => resource.slug === logEntry.slug);
                if (!queryResource) {
                  return null;
                }
                queryResource.searchLog = logEntry;
                return queryResource;
              })
              .filter((resource) => !!resource);
          }));
        }));
      } else {
        this.hasLastQueriedResources = false;
        this.resources = [];
        return of([]);
      }
    }));
  }

  public fetchResourcesFromQuerySubject(): Observable<(ProjectViewModel[]|UserViewModel[]|EventApiModel[]|MarketplaceAdvertisementApiModel[])[]> {
    return this.resultSubject$.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap((query: string) => {
        console.log(query, 'query');
        this.loading = true;
        return this.fetchResources(query);
      })).pipe(tap(() => this.loading = false));
  }

  public removeSearchLog(queryResourceInterface: QueryResourceInterface) {
    return this.searchLogRS.delete(queryResourceInterface.searchLog);
  }

  public fetchResources(
    query: string,
    page: number = 1,
    types: string[] = [],
    exactTag: string | null = null
  ): Observable<(ProjectViewModel[]|UserViewModel[]|EventApiModel[]|MarketplaceAdvertisementApiModel[])[]> {
    return this.fetchResourcesWithMode(query, page, false, types, exactTag);
  }

  public hardRefreshResources(
    query: string,
    page: number = 1,
    types: string[] = [],
    exactTag: string | null = null
  ): Observable<(ProjectViewModel[]|UserViewModel[]|EventApiModel[]|MarketplaceAdvertisementApiModel[])[]> {
    return this.fetchResourcesWithMode(query, page, true, types, exactTag);
  }

  /** Stage a first-page refresh without resetting the current search results. */
  public prepareRefreshResources(query: string, types: string[] = [], exactTag: string | null = null): Observable<() => void> {
    return defer(() => {
      let commit = () => undefined;
      return this.fetchResourcesWithMode(query, 1, true, types, exactTag, apply => commit = apply).pipe(map(() => commit));
    });
  }

  private fetchResourcesWithMode(
    query: string,
    page: number = 1,
    forceRefresh: boolean = false,
    types: string[] = [],
    exactTag: string | null = null,
    stage?: (commit: () => void) => void
  ): Observable<(ProjectViewModel[]|UserViewModel[]|EventApiModel[]|MarketplaceAdvertisementApiModel[])[]> {
    const normalizedQuery = (query || '').trim();
    const limit = 20;
    const offset = Math.max(0, page - 1) * limit;
    if (page === 1 && !stage) {
      this.resetCachedResults();
    }
    const append = page !== 1;

    return this.globalSearchRS.search({
      q: normalizedQuery,
      limit,
      offset,
      types,
      highlight: true,
      forceRefresh,
      exactTag
    }).pipe(map((result) => {
      const items = result.items || [];
      const mapped = items.map((item) => this.mapGlobalItemToResource(item));
      const posts = mapped.filter((resource) => resource.type === 'post');
      const users = mapped.filter((resource) => resource.type === 'user');
      const projects = mapped.filter((resource) => resource.type === 'project' || resource.type === 'vehicle');
      const events = mapped.filter((resource) => resource.type === 'event');
      const marketplace = mapped.filter((resource) => resource.type === 'marketplace' || resource.type === 'advertisement');

      const commit = () => {
        this.posts = append ? [...this.posts, ...posts] : posts;
        this.users = append ? [...this.users, ...users] : users;
        this.projects = append ? [...this.projects, ...projects] : projects;
        this.events = append ? [...this.events, ...events] : events;
        this.marketplace = append ? [...this.marketplace, ...marketplace] : marketplace;
        this.resources = append ? [...this.resources, ...mapped] : mapped;

        const requestedType = types.length === 1 ? types[0] : null;
        this.totalItems.posts = requestedType === 'post' ? (result.total || 0) : this.posts.length;
        this.totalItems.projects = requestedType === 'project' ? (result.total || 0) : this.projects.length;
        this.totalItems.users = requestedType === 'user' ? (result.total || 0) : this.users.length;
        this.totalItems.events = requestedType === 'event' ? (result.total || 0) : this.events.length;
        this.totalItems.marketplace = requestedType === 'advertisement' ? (result.total || 0) : this.marketplace.length;
        this.totalItems.all = result.total || this.resources.length;
        this.hasLastQueriedResources = false;
      };
      if (stage) stage(commit); else commit();

      return [];
    }));

  }

  private mapGlobalItemToResource(item: GlobalSearchItemApiModel): QueryResourceInterface {
    const type = this.mapGlobalType(item.type);
    const title = item.title || item.label || '';
    const subtitle = item.subtitle || '';
    const renderData = item.renderData || {};
    const users = this.mapGlobalUsers(type, renderData);
    return {
      id: item.id,
      name: subtitle || title,
      alias: subtitle ? title : null,
      uri: item.uri || null,
      slug: item.slug,
      type,
      thumbnail: item.thumbnail || 'assets/img/profile-bg-dark.png',
      score: item.score,
      highlight: item.highlight || [],
      renderData,
      subject: renderData,
      users,
      isPlaceholder: false
    };
  }

  private mapGlobalUsers(type: QueryResourceInterface['type'], renderData: any): any[] {
    if (type === 'project') {
      const owner = renderData?.ownerUsername || renderData?.owner;
      return owner ? [{username: owner}] : [];
    }
    if (type === 'marketplace') {
      const owner = renderData?.ownerUsername || renderData?.owner;
      return owner ? [{username: owner}] : [];
    }
    if (type === 'event' && Array.isArray(renderData?.organizers)) {
      return renderData.organizers.map((organizer) => ({username: organizer}));
    }

    return [];
  }

  private mapGlobalType(type: string): QueryResourceInterface['type'] {
    switch (type) {
      case 'advertisement':
        return 'marketplace';
      case 'project':
        return 'project';
      case 'user':
        return 'user';
      case 'event':
        return 'event';
      case 'hashtag':
        return 'hashtag';
      case 'vehicle':
        return 'vehicle';
      case 'post':
        return 'post';
      default:
        return 'project';
    }
  }

  private buildEventQueryParams(baseParams: QueryParamsApiModel): QueryParamsApiModel {
    const eventParams = new QueryParamsApiModel();
    eventParams.filters = [...(baseParams.filters || [])];
    eventParams.page = baseParams.page;
    eventParams.pagination = baseParams.pagination;
    eventParams.limit = baseParams.limit;
    this.appendEventFiltersFromSession(eventParams);
    return eventParams;
  }

  private buildProjectQueryParams(baseParams: QueryParamsApiModel): QueryParamsApiModel {
    const projectParams = new QueryParamsApiModel();
    projectParams.filters = [...(baseParams.filters || [])];
    projectParams.page = baseParams.page;
    projectParams.pagination = baseParams.pagination;
    projectParams.limit = baseParams.limit;
    this.appendProjectFiltersFromSession(projectParams);
    return projectParams;
  }

  private buildUserQueryParams(baseParams: QueryParamsApiModel): QueryParamsApiModel {
    const userParams = new QueryParamsApiModel();
    userParams.filters = [...(baseParams.filters || [])];
    userParams.page = baseParams.page;
    userParams.pagination = baseParams.pagination;
    userParams.limit = baseParams.limit;
    this.appendUserFiltersFromSession(userParams);
    return userParams;
  }

  private buildMarketplaceQueryParams(baseParams: QueryParamsApiModel, query: string): QueryParamsApiModel {
    const marketplaceParams = new QueryParamsApiModel();
    marketplaceParams.page = baseParams.page;
    marketplaceParams.pagination = baseParams.pagination;
    marketplaceParams.limit = baseParams.limit;
    if (query.length) {
      marketplaceParams.filters.push(new FilterParameterApiModel('title', query, 'single'));
      marketplaceParams.filters.push(new FilterParameterApiModel('project.vehicle.vehicleIdentity', query, 'single'));
      marketplaceParams.filters.push(new FilterParameterApiModel('project.alias', query, 'single'));
    }
    this.appendMarketplaceFiltersFromSession(marketplaceParams);
    return marketplaceParams;
  }

  private appendEventFiltersFromSession(queryParams: QueryParamsApiModel): void {
    const stored = QueryParamsService.getNormalizedQueryParametersFromSessionStorage();
    const hasNameFilter = (queryParams.filters || []).some((filter) => filter.name === 'name');
    const isEventFilter = (key: string): boolean => {
      return key === 'addresses.coordinates'
        || key === 'organizers'
        || key === 'name'
        || key === 'hashtags.name'
        || key === 'fromFuture'
        || key === 'order[distance]'
        || key.startsWith('eventDates.');
    };

    Object.entries(stored).forEach(([key, value]) => {
      if (!isEventFilter(key)) {
        return;
      }
      if (key === 'name' && hasNameFilter) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.filters.push(new FilterParameterApiModel(key, item, 'array'));
        });
        return;
      }
      queryParams.filters.push(new FilterParameterApiModel(key, value, 'single'));
    });
  }

  private appendUserFiltersFromSession(queryParams: QueryParamsApiModel): void {
    const stored = QueryParamsService.getNormalizedQueryParametersFromSessionStorage();
    const userKeys = new Set(['info.city.coordinates']);
    Object.entries(stored).forEach(([key, value]) => {
      if (!userKeys.has(key)) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.filters.push(new FilterParameterApiModel(key, item, 'array'));
        });
        return;
      }
      queryParams.filters.push(new FilterParameterApiModel(key, value, 'single'));
    });
  }

  private appendProjectFiltersFromSession(queryParams: QueryParamsApiModel): void {
    const stored = QueryParamsService.getNormalizedQueryParametersFromSessionStorage();
    const projectKeys = new Set(['user.info.city.coordinates', 'attributes.registrationPlate']);
    Object.entries(stored).forEach(([key, value]) => {
      if (!projectKeys.has(key)) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.filters.push(new FilterParameterApiModel(key, item, 'array'));
        });
        return;
      }
      queryParams.filters.push(new FilterParameterApiModel(key, value, 'single'));
    });
  }

  private appendMarketplaceFiltersFromSession(queryParams: QueryParamsApiModel): void {
    const stored = QueryParamsService.getNormalizedQueryParametersFromSessionStorage();
    const allowedExact = new Set([
      'project.vehicle.vehicleType',
      'project.vehicle.make',
      'project.vehicle.bodyType',
      'project.vehicle.model',
      'project.vehicle.engine.engineType',
      'project.vehicle.bodyWork.numOfSeater',
      'project.vehicle.bodyWork.numOfDoors',
      'project.equipment',
      'project.attributes.currentColour',
      'project.attributes.originalColour',
      'project.attributes.originalPaintDiffersCurrent',
      'project.attributes.hasPaintCoverage',
      'project.attributes.hasPpfWrapCoverage',
      'project.attributes.hasOtherWrapCoverage',
      'project.attributes.hasOtherPaintCoverage',
      'project.attributes.imported',
      'project.attributes.accidentFree',
      'project.attributes.damaged',
      'project.attributes.rightSideSteeringWheel',
      'project.attributes.registeredInCountry',
      'project.attributes.asoService',
      'project.attributes.registeredAsAntique',
      'project.attributes.mechanicalTuning',
      'project.attributes.visualTuning',
      'project.attributes.truckApproval',
      'invoiceVat',
      'invoiceVatMargin',
      'toNegotiation',
      'leasingAssignment',
      'addresses.coordinates'
    ]);
    const allowedPrefixes = [
      'project.vehicle.bodyGen.yearFrom',
      'project.vehicle.bodyGen.yearTo',
      'project.vehicle.engine.capacityCm3',
      'project.vehicle.engine.enginePowerBHP',
      'project.attributes.mileage',
      'priceGross',
      'leasingInitialFee',
      'leasingMonthlyInstallment',
      'leasingRemainingInstallments',
      'leasingBuyoutValue'
    ];
    const isMarketplaceFilter = (key: string): boolean => {
      return allowedExact.has(key) || allowedPrefixes.some(prefix => key.startsWith(prefix));
    };

    Object.entries(stored).forEach(([key, value]) => {
      if (!isMarketplaceFilter(key)) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.filters.push(new FilterParameterApiModel(key, item, 'array'));
        });
        return;
      }
      queryParams.filters.push(new FilterParameterApiModel(key, value, 'single'));
    });
  }

  private forkDeps([projectsAM, usersAM, eventsAM, advertisementsAM]: [ResultListApiModel<ProjectApiModel>, ResultListApiModel<UserApiModel>, ResultListApiModel<EventApiModel>, ResultListApiModel<MarketplaceAdvertisementApiModel>], append: boolean): Observable<(ProjectViewModel[]|UserViewModel[]|EventApiModel[]|MarketplaceAdvertisementApiModel[])[]> {
    return forkJoin([
      this.getVehicleCollection(projectsAM.records),
      this.getMediaObjectCollection([...projectsAM.records, ...usersAM.records]),
      this.getUsersAMCollection([...projectsAM.records.map((e: ProjectApiModel) => e.user as string), ...ArrayHelper.flattenArray(eventsAM.records.map((e: EventApiModel) => e.organizers.map(o => o.data)))])
    ]).pipe(map( ([vehicleVMs, mediaObjectVMs, dependantUserAMs]: [VehicleViewModel[], MediaObjectViewModel[], ResultListApiModel<UserApiModel>]) => {
        const projects = projectsAM.records.map((e: ProjectApiModel) => {
          const ownerRecord = dependantUserAMs.records.find(u => u['@id'] === e.user);
          const owner = ownerRecord
            ? new UserViewModel(ownerRecord)
            : (typeof e.user === 'object' ? new UserViewModel(e.user as UserApiModel) : null);
          if (owner) {
            if (!owner.username && owner.slug) {
              owner.username = owner.slug;
            }
          }
          const ownerSlug = owner?.slug || null;
          return Object.assign({
            name: vehicleVMs.find(v => v['@id'] ===  e.vehicle)?.fullVehicleIdentity,
            alias: e.alias,
            uri: ownerSlug ? '/' + ownerSlug + `/garage/` + e.slug : `/garage/${e.slug}`,
            slug: e.slug,
            users: owner ? [owner] : [],
            subject: e,
            thumbnail: MediaObjectPipe.transformToLink(mediaObjectVMs.find(m => m['@id'] ===  e.theme), {defaultImageType: e.vehicleType === VehicleTypeEnum.CAR ? 'car' : 'motorcycle'}),
            type: 'project',
            isPlaceholder: false
          });
        });
        const users = usersAM.records.map((e: UserApiModel) => {
          const user = new UserViewModel(e);
          const publicInfo = user.info instanceof PersonalInformationViewModel ? user.info : null;
          const firstName = publicInfo?.firstName || '';
          const lastName = publicInfo?.lastName || '';
          const fullName = e.name || `${firstName} ${lastName}`.trim();
          return Object.assign({
            name: fullName.length ? fullName : user.username || user.slug || '',
            alias: e.username,
            uri: '/' + e.slug,
            slug: e.slug,
            users: [e],
            subject: e,
            thumbnail: mediaObjectVMs.find(m => m['@id'] ===  e.avatar)?.hyperlinks[0]?.uri || 'assets/img/profile-bg-dark.png',
            type: 'user',
            isPlaceholder: false
          });
        });
        const events = eventsAM.records.map((e: EventApiModel) => {
          return Object.assign({
            name: e.name,
            alias: null,
            uri: '/events/' + e.slug,
            slug: e.slug,
            subject: e,
            users: (e.organizers as ParticipantApiModel[]).map((organizer: ParticipantApiModel) => {
              const organizerData = organizer.data;
              const organizerIri = typeof organizerData === 'string'
                ? organizerData
                : organizerData?.['@id'];
              return dependantUserAMs.records.find(user => user['@id'] === organizerIri)
                || (typeof organizerData === 'object' ? organizerData : null);
            }).filter(Boolean),
            thumbnail: (e.cover as MediaObjectApiModel)?.hyperlinks[0]?.uri || 'assets/img/profile-bg-dark.png',
            type: 'event',
            isPlaceholder: false
          });
        });
        const marketplace = advertisementsAM.records.map((e: MarketplaceAdvertisementApiModel) => {
          return Object.assign({
            name: e.title,
            alias: null,
            uri: '/marketplace/offer/' + e.slug,
            slug: e.slug,
            subject: e,
            users: [e.createdBy],
            thumbnail: (e.media[0] as MediaObjectApiModel)?.hyperlinks[0]?.uri || 'assets/img/profile-bg-dark.png',
            type: 'marketplace',
            isPlaceholder: false
          });
        });
        this.projects = append ? [...this.projects, ...projects] : projects;
        this.users = append ? [...this.users, ...users] : users;
        this.events = append ? [...this.events, ...events] : events;
        this.marketplace = append ? [...this.marketplace, ...marketplace] : marketplace;
        this.resources = [...this.users, ...this.projects, ...this.events, ...this.marketplace].sort(QueryService.sortByName);

        return [
          projectsAM.records.map((projectAM: ProjectApiModel) => new ProjectViewModel(projectAM)),
          usersAM.records.map((userAM: UserApiModel) => new UserViewModel(userAM)),
          eventsAM.records,
          advertisementsAM.records,
        ];
    }));
  }

  private getVehicleCollection(projectAMs: ProjectApiModel[]): Observable<VehicleViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = projectAMs.map(e => new FilterParameterApiModel('id', e.vehicle));
    return this.vehicleRS.getVehicleCollection(params)
      .pipe(mergeMap((resultListAM: ResultListApiModel<VehicleApiModel>) => {
        return this.getVehicleTypeCollection().pipe(map((vehicleTypeVMs: VehicleTypeViewModel[]) => {
          return resultListAM.records.map((vehicleAM: VehicleApiModel) => {
            const vehicleVM = new VehicleViewModel(vehicleAM);
            vehicleVM.vehicleType = vehicleTypeVMs.find(e => e['@id'] === vehicleAM.vehicleType);
            return vehicleVM;
          });
        }));
      }));
  }

  private getVehicleTypeCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<VehicleTypeViewModel[]> {
    return this.vehicleTypeRS.getVehicleTypeCollection(queryParamsApiModel)
      .pipe(map((resultListAM: ResultListApiModel<VehicleTypeApiModel>) => {
        return resultListAM.records.map((vehicleAM: VehicleTypeApiModel) => new VehicleTypeViewModel(vehicleAM));
      }));
  }

  private getUsersAMCollection(userLinks: any[]): Observable<ResultListApiModel<UserApiModel>> {
    const params = new QueryParamsApiModel();
    const slugs = userLinks
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.split('/').pop();
        }
        if (entry && typeof entry === 'object') {
          if (typeof entry.slug === 'string') {
            return entry.slug;
          }
          if (typeof entry.username === 'string') {
            return entry.username;
          }
          if (typeof entry['@id'] === 'string') {
            return entry['@id'].split('/').pop();
          }
          if (typeof entry.data === 'string') {
            return entry.data.split('/').pop();
          }
        }

        return null;
      })
      .filter((slug): slug is string => !!slug);
    if (!slugs.length) {
      return of(new ResultListApiModel<UserApiModel>(UserApiModel));
    }
    params.filters = slugs.map((slug) => new FilterParameterApiModel('slug', slug));
    return this.userRS.getCollection(params);
  }

  private getMediaObjectCollection(resultAMs: (ProjectApiModel|UserApiModel)[]): Observable<MediaObjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = resultAMs
      .filter((e: ProjectApiModel|UserApiModel) => typeof e['theme'] === 'string' || typeof e['avatar'] === 'string')
      .map(e => new FilterParameterApiModel('id', e['theme'] || e['avatar']));
    if (params.filters.length) {
      return this.mediaObjectRS.getMediaObjectCollection(params)
        .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
          return resultListAM.records.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM));
        }));
    }

    return of<MediaObjectViewModel[]>([]);
  }

  public resetCachedResults(): void {
    this.resources = [];
    this.posts = [];
    this.users = [];
    this.events = [];
    this.projects = [];
    this.marketplace = [];
    this.totalItems = {all: 0, posts: 0, projects: 0, users: 0, events: 0, marketplace: 0};
  }
}
